import { Fragment, useState, useEffect, useContext, useRef, useCallback, useMemo, memo } from 'react'
import axios from "axios";

import { get, ref } from "firebase/database";

import { db } from '../utils/firebaseApp.js';
import { AuthContext } from "../utils/auth.js";

import { Menu, Dialog, Transition } from '@headlessui/react'
import { ExclamationTriangleIcon, CheckIcon, TableCellsIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline'
import { ArrowLeftIcon, ArrowRightIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { TrashIcon as CustomTrashIcon, ArrowDownTrayIcon as CustomDownloadIcon, ClipboardDocumentIcon as CustomShareIcon } from '@heroicons/react/20/solid'
import { XMarkIcon } from '@heroicons/react/20/solid'

import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import toast from 'react-hot-toast';

import ImageViewerWithHighlights from './ImageViewerWithHighlights.js';
import PDFViewerWithHighlights from './PDFViewerWithHighlights.js';

import ExportDialog from './ExportDialog.js';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

function formatFileStatus(status) {
    return status === "waiting" ? "processing" : status;
}

function getFileStatusClasses(status) {
    if (status === "confirmed") return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
    if (status === "processed") return "bg-indigo-50 text-indigo-700 ring-indigo-600/20";
    if (status === "waiting") return "bg-amber-50 text-amber-700 ring-amber-600/20";
    if (status === "error") return "bg-red-50 text-red-700 ring-red-600/20";
    return "bg-gray-50 text-gray-700 ring-gray-600/20";
}

const ViewFile = ({ initialFileStatus, fileData, extractionId, batchId, extraction, closeModal }) => {
    const { currentUser } = useContext(AuthContext);
    let userId = currentUser.uid;

    const serverUrl = process.env.REACT_APP_SERVER_URL;

    const [isLoading, setIsLoading] = useState(false);
    const [showHighlights, setShowHighlights] = useState(true);

    const [fileUrl, setFileUrl] = useState(fileData.file_url);
    const [fileName, setFileName] = useState(fileData.file_name);
    const [fileId, setFileId] = useState(fileData.file_id);
    const [fileStatus, setFileStatus] = useState(fileData.status);
    const [fileCoordinates, setFileCoordinates] = useState(fileData.coordinates);
    const [fileDocumentInfo, setFileDocumentInfo] = useState(fileData.documentInfo);

    const noCoordinates = !fileCoordinates || Object.keys(fileCoordinates).length === 0;

    const [showDeleteFile, setShowDeleteFile] = useState(false);

    const [files, setFiles] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);

    const inputRefs = useRef({});
    const [selectedKey, setSelectedKey] = useState(null);
    const [mobileView, setMobileView] = useState('data');

    const prepereData = (data) => {
        let newData = {};
        extraction.fields.forEach((field) => {
            let key = field.key;
            let fieldType = field.type ?? 'string';
            let itemsType = field.items?.type ?? 'string';

            if (fieldType === "string") {
                newData[key] = data[key] ?? '';
            } else if (fieldType === "object") {
                let defaultObject = {};
                field.properties.forEach((innerField) => {
                    defaultObject[innerField.key] = '';
                });
                newData[key] = data[key] ?? defaultObject;
            } else if (fieldType === "array" && itemsType === "string") {
                newData[key] = data[key] ?? [''];
            } else if (fieldType === "array" && itemsType === "object") {
                let defaultObject = {};
                field.items.properties.forEach((innerField) => {
                    defaultObject[innerField.key] = '';
                });
                newData[key] = data[key] ?? [defaultObject];
            }
        });
        return newData;
    }

    const [data, setData] = useState(prepereData(fileData.data));
    const [initialData, setInitialData] = useState(prepereData(fileData.data));

    const handleFieldClick = useCallback((key) => {
        setMobileView('data');
        setSelectedKey(key);
        const input = inputRefs.current[key];
        if (input) {
            // First scroll the input into view
            input.scrollIntoView({
                behavior: "smooth",
                block: "center",
                inline: "center",
            });

            // Then focus the input after a short delay to ensure scroll is complete
            setTimeout(() => {
                input.focus();
            }, 500); // Adjust timing if needed
        }
    }, []);

    const updateFileResult = async () => {
        await currentUser.getIdToken().then(async (idToken) => {
            const url = serverUrl + "/updateFileResult";

            await axios.post(url, {
                "extractionId": extractionId,
                "batchId": batchId,
                "fileId": fileId,
                "data": data,
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                }
            }).then(async (response) => {
                if (response.status === 200) {
                    toast.success('File result updated successfully!');

                    setInitialData(data);
                    setData(data);

                    // update files data
                    let newFiles = files.map((file) => {
                        if (file.id === fileId) {
                            file.result = data;
                        }
                        return file;
                    });
                    setFiles(newFiles);
                } else {
                    toast.error('Error updating file result!');
                }
            }).catch((error) => {
                toast.error('Error updating file result!');
            });
        });
    }

    const handleInputChangeString = useCallback((key, value) => {
        setData((previousData) => ({
            ...previousData, [key]: value
        }));
    }, []);

    const handleInputChangeObject = useCallback((key, subKey, value) => {
        setData((previousData) => ({
            ...previousData,
            [key]: {
                ...previousData[key],
                [subKey]: value
            }
        }));
    }, []);

    const handleInputChangeArrayString = useCallback((key, index, value) => {
        setData((previousData) => ({
            ...previousData,
            [key]: previousData[key].map((item, i) => {
                if (i === index) {
                    return value;
                } else {
                    return item;
                }
            })
        }));
    }, []);

    const handleInputChangeArrayObject = useCallback((key, index, subKey, value) => {
        setData((previousData) => ({
            ...previousData,
            [key]: previousData[key].map((item, i) => {
                if (i === index) {
                    return {
                        ...item,
                        [subKey]: value
                    };
                } else {
                    return item;
                }
            })
        }));
    }, []);

    const addInputArrayString = useCallback((key) => {
        setData((previousData) => {
            if (previousData[key].some((item) => item === '')) {
                return previousData;
            }

            return {
                ...previousData,
                [key]: [...previousData[key], '']
            };
        });
    }, []);

    const addInputArrayObject = useCallback((key) => {
        setData((previousData) => {
            if (previousData[key].some((item) => Object.values(item).every((value) => value === ''))) {
                return previousData;
            }

            let defaultObject = {};
            let fields = extraction.fields.find((field) => field.key === key).items.properties;

            fields.forEach((innerField) => {
                defaultObject[innerField.key] = '';
            });

            return {
                ...previousData,
                [key]: [...previousData[key], defaultObject]
            };
        });
    }, [extraction]);

    const deleteInputArrayString = useCallback((key, index) => {
        setData((previousData) => ({
            ...previousData,
            [key]: previousData[key].filter((item, i) => i !== index)
        }));
    }, []);

    const deleteInputArrayObject = useCallback((key, index) => {
        setData((previousData) => ({
            ...previousData,
            [key]: previousData[key].filter((item, i) => i !== index)
        }));
    }, []);

    const getFileTypeFromUrl = (url) => {
        try {
            const path = url.split('?')[0];
            const extension = path.split('.').pop();
            if (extension === 'heic') return 'heic';
            if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'jfif'].includes(extension)) return 'image';
            return extension;
        } catch (error) {
            return 'error';
        }
    };

    const updateFileStatus = async () => {
        await currentUser.getIdToken().then(async (idToken) => {
            const url = serverUrl + "/updateFileStatus";

            await axios.post(url, {
                "extractionId": extractionId,
                "batchId": batchId,
                "fileId": fileId,
                "status": "confirmed",
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                }
            }).then(async (response) => {
                if (response.status === 200) {
                    setFileStatus("confirmed");
                    toast.success('File status updated successfully!');

                    // update files data
                    let newFiles = files.map((file) => {
                        if (file.id === fileId) {
                            file.status = "confirmed";
                        }
                        return file;
                    });
                    setFiles(newFiles);

                    // go to next file if there is one, or go to previous or close the modal
                    if (isNextFile()) {
                        await nextFile(true);
                    } else if (isPreviousFile()) {
                        await previousFile(true);
                    } else {
                        await closePopup(true);
                    }
                } else {
                    toast.error('Error updating file status!');
                }
            }).catch((error) => {
                toast.error('Error updating file status!');
            });
        });
    }

    const dataChanged = useMemo(
        () => JSON.stringify(data) !== JSON.stringify(initialData),
        [data, initialData]
    );

    const hasDataChanged = useCallback(() => dataChanged, [dataChanged]);

    const isNextFile = () => {
        let currentFileId = fileId;
        let index = files.findIndex((file) => file.id === currentFileId);
        if (index === -1) return false;
        if (index === files.length - 1) return false;
        return true;
    }

    const isPreviousFile = () => {
        let currentFileId = fileId;
        let index = files.findIndex((file) => file.id === currentFileId);
        if (index === -1) return false;
        if (index === 0) return false;
        return true;
    }

    const nextFile = async (skipCheck = false) => {
        try {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 200));

            const isChangedData = hasDataChanged();

            if (isChangedData && !skipCheck) {
                setNextMoveAfterSaving('next');
                setShowUnsavedChangesAlert(true);
                return;
            }

            let currentFileId = fileId;
            let index = files.findIndex((file) => file.id === currentFileId);
            if (index === -1) return;
            if (index === files.length - 1) return;
            let nextFileId = files[index + 1].id;
            let nextFileData = files[index + 1];

            setFileId(nextFileId);
            setFileUrl(nextFileData.url);
            setFileName(nextFileData.name);
            setFileStatus(nextFileData.status);
            setData(prepereData(nextFileData.result ?? {}));
            setFileCoordinates(nextFileData.coordinates ?? {});
            setFileDocumentInfo(nextFileData.documentInfo ?? {});
            setInitialData(prepereData(nextFileData.result ?? {}));
            setCurrentIndex(index + 1);
        } catch (error) {
        }

        setIsLoading(false);
    }

    const previousFile = async (skipCheck = false) => {
        try {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 200));

            const isChangedData = hasDataChanged();

            if (isChangedData && !skipCheck) {
                setNextMoveAfterSaving('previous');
                setShowUnsavedChangesAlert(true);
                return;
            }

            let currentFileId = fileId;
            let index = files.findIndex((file) => file.id === currentFileId);
            if (index === -1) return;
            if (index === 0) return;
            let previousFileId = files[index - 1].id;
            let previousFileData = files[index - 1];

            setFileId(previousFileId);
            setFileUrl(previousFileData.url);
            setFileName(previousFileData.name);
            setFileStatus(previousFileData.status);
            setFileCoordinates(previousFileData.coordinates ?? {});
            setFileDocumentInfo(previousFileData.documentInfo ?? {});
            setData(prepereData(previousFileData.result ?? {}));
            setInitialData(prepereData(previousFileData.result ?? {}));
            setCurrentIndex(index - 1);
        } catch (error) {
        }
        setIsLoading(false);
    }

    const closePopup = async (skipCheck = false) => {
        try {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 200));

            const isChangedData = hasDataChanged();

            if (isChangedData && !skipCheck) {
                setNextMoveAfterSaving('close');
                setShowUnsavedChangesAlert(true);
                return;
            }

            closeModal();
        } catch (error) {

        }
    }

    const deleteFile = async () => {
        await currentUser.getIdToken().then(async (idToken) => {
            const url = serverUrl + "/delete";

            await axios.post(url, {
                "extractionId": extractionId,
                "batchId": batchId,
                "fileId": fileId,
            }, {
                headers: {
                    "Content-Type": "application/json",
                    "Access-Control-Allow-Origin": "*",
                    Authorization: `Bearer ${idToken}`
                }
            }).then(async (response) => {
                setShowDeleteFile(false);

                if (response.status === 200) {
                    toast.success('File deleted successfully!');

                    await fetchFiles();

                    if (isNextFile()) {
                        await nextFile(true);
                    } else if (isPreviousFile()) {
                        await previousFile(true);
                    } else {
                        await closePopup(true);
                    }
                } else {
                    toast.error('Error deleting file!');
                }
            }).catch((error) => {
                setShowDeleteFile(false);
                toast.error('Error deleting file!');
            });
        });
    }

    const copyFileLink = async () => {
        navigator.clipboard.writeText(fileUrl);
        toast.success('File link copied to clipboard!');
    }

    const downloadFile = async () => {
        window.open(fileUrl, '_blank');
        toast.success('File opened successfully!');
    };

    const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
    const [nextMoveAfterSaving, setNextMoveAfterSaving] = useState('');
    const [showExportModal, setShowExportModal] = useState(false);
    const [showExpandedResult, setShowExpandedResult] = useState(false);
    const [showExpandedDocument, setShowExpandedDocument] = useState(false);

    const fetchFiles = async () => {
        // load all files and put them in the state
        let files = [];

        let userId = currentUser.uid;
        const databaseRef = ref(db, 'files/' + userId + '/' + extractionId + '/' + batchId);

        try {
            const snapshot = await get(databaseRef);

            if (snapshot.exists()) {
                const data = snapshot.val();

                if (data) {
                    for (let fileId in data) {

                        let file = data[fileId];
                        file.id = fileId;

                        if (initialFileStatus === 'All') {
                            files.push(file);
                        } else if ((file.status === 'processed' || file.status === 'waiting') && initialFileStatus === 'To Review') {
                            files.push(file);
                        } else if (file.status === 'confirmed' && initialFileStatus === 'Confirmed') {
                            files.push(file);
                        } else if (file.status === 'error' && initialFileStatus === 'Error') {
                            files.push(file);
                        }
                    }

                    setFiles(files);
                }
            } else {
                console.log("No data available");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            // load all files and put them in the state
            let files = [];

            let userId = currentUser.uid;
            const databaseRef = ref(db, 'files/' + userId + '/' + extractionId + '/' + batchId);

            try {
                const snapshot = await get(databaseRef);

                if (snapshot.exists()) {
                    const data = snapshot.val();

                    if (data) {
                        for (let fileId in data) {

                            let file = data[fileId];
                            file.id = fileId;

                            if (initialFileStatus === 'All') {
                                files.push(file);
                            } else if ((file.status === 'processed' || file.status === 'waiting') && initialFileStatus === 'To Review') {
                                files.push(file);
                            } else if (file.status === 'confirmed' && initialFileStatus === 'Confirmed') {
                                files.push(file);
                            } else if (file.status === 'error' && initialFileStatus === 'Error') {
                                files.push(file);
                            }
                        }

                        setFiles(files);
                    }
                } else {
                    console.log("No data available");
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        }
        fetchData();
    }, [extractionId, batchId, currentUser, initialFileStatus]);

    const transformDBDataToPages = useCallback((dbData, documentInfo) => {
        //documentInfo is an object with the following properties:
        //pages: an array of objects with the following properties:
        //pageNumber: the page number
        //width: the width of the page
        //height: the height of the page
        //units: the units of the page
        //and totalPages: the total number of pages

        if (!documentInfo || !documentInfo.pages) {
            return [];
        }

        const pagesMap = {};

        //loop through documentinfo and initialize for every page the sizes
        if (documentInfo.pages) {
            documentInfo.pages.forEach(page => {
                pagesMap[page.pageNumber] = {
                    pageNumber: page.pageNumber,
                    originalWidth: page.width,
                    originalHeight: page.height,
                    fields: [],
                };
            });
        }

        Object.entries(dbData).forEach(([key, value]) => {
            //key doesnt contain box field
            //check if type of value is list
            if (Array.isArray(value)) {
                //foreach value in the list
                value.forEach((item, index) => {
                    if (item.isEmpty) {
                    } else if (!item.box) {
                        //go one level deepe
                        Object.entries(item).forEach(([subKey, subValue]) => {
                            const { pageNumber, box, isEmpty } = subValue;
                            if (isEmpty) {

                            } else {
                                const field = {
                                    topRightX: box.bottom_right.x,
                                    topRightY: box.top_left.y,
                                    bottomLeftX: box.top_left.x,
                                    bottomLeftY: box.bottom_right.y,
                                    key: key + "_" + (index + 1) + "_" + subKey,
                                    commonKeys: [key + "_" + (index + 1) + "_" + subKey]
                                };
                                const existingField = pagesMap[pageNumber].fields.find(e => field.topRightX === e.topRightX && field.topRightY === e.topRightY && field.bottomLeftX === e.bottomLeftX && field.bottomLeftY === e.bottomLeftY);
                                if (existingField) {
                                    existingField.commonKeys.push(key + "_" + (index + 1) + "_" + subKey);
                                } else {
                                    pagesMap[pageNumber].fields.push(field);
                                }
                            }
                        });
                    } else {
                        const { pageNumber, box, isEmpty } = item;
                        if (isEmpty) {

                        } else {
                            const field = {
                                topRightX: box.bottom_right.x,
                                topRightY: box.top_left.y,
                                bottomLeftX: box.top_left.x,
                                bottomLeftY: box.bottom_right.y,
                                key: key + "_" + (index + 1),
                                commonKeys: [key + "_" + (index + 1)]
                            };

                            //if there is a field with same sizes exists already and if so add the key to the existing field
                            const existingField = pagesMap[pageNumber].fields.find(e => field.topRightX === e.topRightX && field.topRightY === e.topRightY && field.bottomLeftX === e.bottomLeftX && field.bottomLeftY === e.bottomLeftY);
                            if (existingField) {
                                existingField.commonKeys.push(key + "_" + (index + 1));
                            } else {
                                pagesMap[pageNumber].fields.push(field);
                            }
                        }
                    }
                });
            } else if (!value.box) {
                //go one level deeper
                Object.entries(value).forEach(([subKey, subValue]) => {
                    const { pageNumber, box } = subValue;
                    const field = {
                        topRightX: box.bottom_right.x,
                        topRightY: box.top_left.y,
                        bottomLeftX: box.top_left.x,
                        bottomLeftY: box.bottom_right.y,
                        key: key + "_" + subKey,
                        commonKeys: [key + "_" + subKey]
                    };
                    const existingField = pagesMap[pageNumber].fields.find(e => field.topRightX === e.topRightX && field.topRightY === e.topRightY && field.bottomLeftX === e.bottomLeftX && field.bottomLeftY === e.bottomLeftY);
                    if (existingField) {
                        existingField.commonKeys.push(key + "_" + subKey);
                    } else {
                        pagesMap[pageNumber].fields.push(field);
                    }
                });
            } else {
                const { pageNumber, box } = value;
                const field = {
                    topRightX: box.bottom_right.x,
                    topRightY: box.top_left.y,
                    bottomLeftX: box.top_left.x,
                    bottomLeftY: box.bottom_right.y,
                    key: key,
                    commonKeys: [key]
                };

                //if there is a field with same sizes exists already and if so add the key to the existing field
                const existingField = pagesMap[pageNumber].fields.find(e => field.topRightX === e.topRightX && field.topRightY === e.topRightY && field.bottomLeftX === e.bottomLeftX && field.bottomLeftY === e.bottomLeftY);
                if (existingField) {
                    existingField.commonKeys.push(key);
                } else {
                    pagesMap[pageNumber].fields.push(field);
                }
            }
        });

        return Object.values(pagesMap);
    }, []);

    const pagesData = useMemo(
        () => transformDBDataToPages(fileCoordinates, fileDocumentInfo),
        [transformDBDataToPages, fileCoordinates, fileDocumentInfo]
    );

    const wrapperRef = useRef(null);
    const fieldRefs = useRef({});

    const handleInputFocus = useCallback((key) => {
        // 🔍 Find the field that this key belongs to (in commonKeys)
        const boxField = pagesData
            .flatMap((page) => page.fields)
            .find((field) => field.commonKeys.includes(key));

        if (boxField) {
            setSelectedKey(key); // for highlighting
            const primaryKey = boxField.key; // only this one has a ref

            const highlight = fieldRefs.current[primaryKey];
            if (highlight) {
                highlight.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "center",
                });
            }
        } else {
            setSelectedKey(null);
            // Only show toast when highlights are enabled
            if (showHighlights && !noCoordinates) {
                toast('No highlight found for this field', {
                    position: 'bottom-center',
                    duration: 1000,
                    style: {
                        background: '#333',
                        color: '#fff',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    },
                });
            }
        }
    }, [pagesData, showHighlights, noCoordinates]);

    useEffect(() => {
        const handleFocusChange = () => {
            setTimeout(() => {
                if (
                    wrapperRef.current &&
                    !wrapperRef.current.contains(document.activeElement)
                ) {
                    setSelectedKey(null); // 👈 Deselect highlight
                }
            }, 0); // Allow time for focus to shift
        };

        document.addEventListener("focusin", handleFocusChange);
        document.addEventListener("focusout", handleFocusChange);

        return () => {
            document.removeEventListener("focusin", handleFocusChange);
            document.removeEventListener("focusout", handleFocusChange);
        };
    }, []);

    const fileType = getFileTypeFromUrl(fileUrl);
    const hasPreview = ['pdf', 'image', 'heic'].includes(fileType);

    return (
        <div className='flex h-full min-h-full w-full flex-col overflow-hidden rounded-lg bg-white shadow-xl'>
            <ShowDeleteModalFile
                open={showDeleteFile}
                setOpen={setShowDeleteFile}
                deleteFunction={deleteFile}
            />

            <ExportDialog
                open={showExportModal}
                setOpen={setShowExportModal}
                fileId={fileId}
                files={files}
                extraction={extraction}
                userId={userId}
                extractionId={extractionId}
            />

            <UnsavedChangesAlert
                open={showUnsavedChangesAlert}
                setOpen={setShowUnsavedChangesAlert}
                nextMoveAfterSaving={nextMoveAfterSaving}
                saveChanges={async () => {
                    await updateFileResult();

                    if (nextMoveAfterSaving === 'next') await nextFile(true);
                    if (nextMoveAfterSaving === 'previous') await previousFile(true);
                    if (nextMoveAfterSaving === 'close') await closePopup(true);

                    setShowUnsavedChangesAlert(false);
                    setIsLoading(false);
                }}
                discardChanges={async () => {
                    if (nextMoveAfterSaving === 'next') await nextFile(true);
                    if (nextMoveAfterSaving === 'previous') await previousFile(true);
                    if (nextMoveAfterSaving === 'close') await closePopup(true);

                    setShowUnsavedChangesAlert(false);
                    setIsLoading(false);
                }}
            />

            <ExpandedResultView
                open={showExpandedResult}
                setOpen={setShowExpandedResult}
                fileName={fileName}
                result={data}
                extraction={extraction}
                handleInputChangeString={handleInputChangeString}
                handleInputChangeObject={handleInputChangeObject}
                handleInputChangeArrayString={handleInputChangeArrayString}
                handleInputChangeArrayObject={handleInputChangeArrayObject}
                addInputArrayString={addInputArrayString}
                addInputArrayObject={addInputArrayObject}
                deleteInputArrayString={deleteInputArrayString}
                deleteInputArrayObject={deleteInputArrayObject}
                hasDataChanged={hasDataChanged}
                saveChanges={updateFileResult}
                isLoading={isLoading}
                onInputFocus={handleInputFocus}
            />

            <ExpandedDocumentView
                open={showExpandedDocument}
                setOpen={setShowExpandedDocument}
                fileName={fileName}
            >
                {showExpandedDocument && (
                    <DocumentPreview
                        fileType={fileType}
                        fileUrl={fileUrl}
                        fileName={fileName}
                        pagesData={pagesData}
                        fileDocumentInfo={fileDocumentInfo}
                        handleFieldClick={handleFieldClick}
                        selectedKey={selectedKey}
                        fieldRefs={fieldRefs}
                        inputRefs={inputRefs}
                        showHighlights={showHighlights}
                        setShowHighlights={setShowHighlights}
                        noCoordinates={noCoordinates}
                    />
                )}
            </ExpandedDocumentView>

            <header className="flex flex-shrink-0 flex-col gap-2 border-b border-gray-200 bg-white px-4 py-2 md:px-5">
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-lg font-semibold text-gray-950 md:text-xl">
                                File view
                            </h2>
                            {!isLoading && (
                                <span className={classNames(
                                    "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium capitalize ring-1 ring-inset",
                                    getFileStatusClasses(fileStatus)
                                )}>
                                    {formatFileStatus(fileStatus)}
                                </span>
                            )}
                        </div>
                        <div className="mt-1 min-w-0 text-sm text-gray-600">
                            {isLoading && <Skeleton width={180} height={18} />}
                            {!isLoading && (
                                <p className="truncate font-medium text-gray-700" title={fileName}>
                                    {fileName}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-shrink-0 items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setShowExpandedResult(true)}
                            className="hidden items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:inline-flex"
                        >
                            <TableCellsIcon className="h-5 w-5 text-indigo-500" aria-hidden="true" />
                            Table view
                        </button>
                        <button
                            type="button"
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            onClick={async () => await closePopup()}
                        >
                            <span className="sr-only">Close</span>
                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-between gap-3 md:hidden">
                    <div className="inline-flex items-center rounded-md bg-gray-50 p-1">
                        <button
                            type="button"
                            onClick={() => setMobileView('data')}
                            className={classNames(
                                "rounded px-3 py-1.5 text-sm font-semibold",
                                mobileView === 'data' ? "bg-white text-indigo-600 shadow-sm" : "text-gray-600"
                            )}
                        >
                            Data
                        </button>
                        {hasPreview && (
                            <button
                                type="button"
                                onClick={() => setMobileView('document')}
                                className={classNames(
                                    "rounded px-3 py-1.5 text-sm font-semibold",
                                    mobileView === 'document' ? "bg-white text-indigo-600 shadow-sm" : "text-gray-600"
                                )}
                            >
                                Document
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <main className={classNames(
                "flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50 p-3 md:gap-4 md:overflow-hidden md:p-4",
                hasPreview ? "md:grid md:grid-cols-[minmax(0,1.35fr)_minmax(360px,0.85fr)]" : "md:block"
            )}>
                {hasPreview && (
                    <section className={classNames(
                        "min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:flex md:flex-col",
                        mobileView !== 'document' && "hidden md:flex"
                    )}>
                        <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-3 py-2">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Document</h3>
                                <p className="text-xs text-gray-500">Click highlighted fields to jump into the result.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowExpandedDocument(true)}
                                className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-white text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                title="Full view"
                            >
                                <span className="sr-only">Expand document</span>
                                <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="h-[58vh] min-h-[360px] overflow-auto bg-slate-100 md:h-auto md:min-h-0 md:flex-1">
                            {!showExpandedDocument && (
                                <DocumentPreview
                                    fileType={fileType}
                                    fileUrl={fileUrl}
                                    fileName={fileName}
                                    pagesData={pagesData}
                                    fileDocumentInfo={fileDocumentInfo}
                                    handleFieldClick={handleFieldClick}
                                    selectedKey={selectedKey}
                                    fieldRefs={fieldRefs}
                                    inputRefs={inputRefs}
                                    showHighlights={showHighlights}
                                    setShowHighlights={setShowHighlights}
                                    noCoordinates={noCoordinates}
                                />
                            )}
                        </div>
                    </section>
                )}

                <section className={classNames(
                    "min-h-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:flex md:flex-col",
                    mobileView !== 'data' && "hidden md:flex",
                    !hasPreview && "md:h-full"
                )}>
                    <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-3 py-3">
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900">Extracted data</h3>
                            <p className="text-xs text-gray-500">{extraction.fields.length} fields</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setShowExpandedResult(true)}
                            className="inline-flex items-center justify-center gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:hidden"
                        >
                            <TableCellsIcon className="h-5 w-5 text-indigo-500" aria-hidden="true" />
                            Table view
                        </button>
                    </div>

                    <div
                        ref={wrapperRef}
                        className='min-h-0 overflow-visible p-3 display-result-section md:flex-1 md:overflow-auto'>
                        <DisplayResult
                            extractionId={extractionId}
                            batchId={batchId}
                            fileId={fileId}
                            result={data}
                            extraction={extraction}
                            handleInputChangeString={handleInputChangeString}
                            handleInputChangeObject={handleInputChangeObject}
                            handleInputChangeArrayString={handleInputChangeArrayString}
                            handleInputChangeArrayObject={handleInputChangeArrayObject}
                            addInputArrayString={addInputArrayString}
                            addInputArrayObject={addInputArrayObject}
                            deleteInputArrayString={deleteInputArrayString}
                            deleteInputArrayObject={deleteInputArrayObject}
                            isLoading={isLoading}
                            inputRefs={inputRefs}
                            onInputFocus={handleInputFocus}
                        />
                    </div>

                    <footer className='flex-shrink-0 border-t border-gray-200 bg-white p-3'>
                        {!hasDataChanged() && fileStatus === 'processed' && (
                            <button
                                onClick={updateFileStatus}
                                className={classNames(
                                    "inline-flex w-full items-center justify-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                                    isLoading && 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                <CheckIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                Confirm
                            </button>
                        )}

                        {!hasDataChanged() && fileStatus === 'confirmed' && (
                            <button
                                onClick={updateFileStatus}
                                className="inline-flex w-full cursor-not-allowed items-center justify-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white opacity-50 shadow-sm"
                                disabled
                            >
                                <CheckIcon className="-ml-0.5 h-5 w-5" aria-hidden="true" />
                                Confirmed
                            </button>
                        )}

                        {hasDataChanged() && (
                            <button
                                onClick={updateFileResult}
                                className={classNames(
                                    "inline-flex w-full items-center justify-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                                    (!hasDataChanged() || isLoading) && 'opacity-50 cursor-not-allowed'
                                )}
                                disabled={!hasDataChanged() || isLoading}
                            >
                                Save result
                            </button>
                        )}

                        <div className="mt-3 inline-flex w-full items-center justify-between rounded-md bg-gray-50 px-2 py-1">
                            <button
                                onClick={async () => await previousFile()}
                                className={classNames(
                                    "inline-flex h-8 w-8 items-center justify-center rounded-md text-indigo-500 hover:bg-white hover:text-indigo-700",
                                    (!isPreviousFile() || isLoading) && 'opacity-50 cursor-not-allowed disabled:text-grey-500'
                                )}
                                disabled={!isPreviousFile() || isLoading}
                            >
                                <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <div className='mx-3 min-w-[4.5rem] text-center text-sm font-medium text-gray-700'>
                                <p>{currentIndex + 1} of {files.length}</p>
                            </div>
                            <button
                                onClick={async () => await nextFile()}
                                className={classNames(
                                    "inline-flex h-8 w-8 items-center justify-center rounded-md text-indigo-500 hover:bg-white hover:text-indigo-700",
                                    (!isNextFile() || isLoading) && 'opacity-50 cursor-not-allowed'
                                )}
                                disabled={!isNextFile() || isLoading}
                            >
                                <ArrowRightIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                        </div>

                        <div className='mt-3 grid grid-cols-4 gap-2'>
                            <button
                                onClick={async () => setShowDeleteFile(true)}
                                className="inline-flex h-10 items-center justify-center rounded-md text-gray-500 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:text-gray-700"
                            >
                                <CustomTrashIcon className="h-5 w-5" aria-hidden="true" />
                            </button>

                            <button
                                onClick={async () => await downloadFile()}
                                className="inline-flex h-10 items-center justify-center rounded-md text-gray-500 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:text-gray-700"
                            >
                                <CustomDownloadIcon className="h-5 w-5" aria-hidden="true" />
                            </button>

                            <button
                                onClick={async () => await copyFileLink()}
                                className="inline-flex h-10 items-center justify-center rounded-md text-gray-500 ring-1 ring-inset ring-gray-200 hover:bg-gray-50 hover:text-gray-700"
                            >
                                <CustomShareIcon className="h-5 w-5" aria-hidden="true" />
                            </button>

                            <Menu as="div" className="relative inline-block text-left overflow-visible">
                                <div className='z-50' onClick={() => setShowExportModal(true)}>
                                    <Menu.Button className="inline-flex h-10 w-full justify-center gap-x-1.5 rounded-md bg-white px-2.5 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                                        Export
                                        <ChevronRightIcon className="-mr-1 h-5 w-5 text-gray-400" aria-hidden="true" />
                                    </Menu.Button>
                                </div>
                            </Menu>
                        </div>
                    </footer>
                </section>
            </main>
        </div>
    );
};

function UnsavedChangesAlert({ open, setOpen, nextMoveAfterSaving, saveChanges, discardChanges }) {
    const cancelButtonRef = useRef(null)

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog className="relative z-100" initialFocus={cancelButtonRef} onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-100 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                Unsaved Changes
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                {nextMoveAfterSaving === 'next' && (
                                                    <p className="text-sm text-gray-500">
                                                        You have unsaved changes. Do you want to save them before procedding to the next file?
                                                    </p>)}

                                                {nextMoveAfterSaving === 'previous' && (
                                                    <p className="text-sm text-gray-500">
                                                        You have unsaved changes. Do you want to save them before procedding to the previous file?
                                                    </p>)}

                                                {nextMoveAfterSaving === 'close' && (
                                                    <p className="text-sm text-gray-500">
                                                        You have unsaved changes. Do you want to save them before closing the modal?
                                                    </p>)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                                        onClick={saveChanges}
                                    >
                                        Save Changes
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={discardChanges}
                                        ref={cancelButtonRef}
                                    >
                                        Discard Changes
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

function ShowDeleteModalFile({ open, setOpen, deleteFunction }) {
    const cancelButtonRef = useRef(null)

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-100" initialFocus={cancelButtonRef} onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-100 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                                        </div>
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                                                Confirm Deletion
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    Are you sure you want to delete this file? This action cannot be undone.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                        onClick={() => deleteFunction()}
                                    >
                                        Delete
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={() => setOpen(false)}
                                        ref={cancelButtonRef}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

function getSchemaOrderedKeys(value = {}, properties = []) {
    const schemaKeys = [...properties]
        .sort((a, b) => {
            const idA = a.id ?? Number.MAX_SAFE_INTEGER;
            const idB = b.id ?? Number.MAX_SAFE_INTEGER;
            return idA - idB;
        })
        .map((property) => property.key);

    const valueKeys = Object.keys(value ?? {});
    const extraKeys = valueKeys.filter((key) => !schemaKeys.includes(key));

    return [...schemaKeys, ...extraKeys];
}

function getArrayObjectColumns(field, rows) {
    const properties = field.items?.properties ?? [];
    const schemaKeys = [...properties]
        .sort((a, b) => {
            const idA = a.id ?? Number.MAX_SAFE_INTEGER;
            const idB = b.id ?? Number.MAX_SAFE_INTEGER;
            return idA - idB;
        })
        .map((property) => property.key);

    const rowKeys = rows.reduce((keys, row) => {
        Object.keys(row ?? {}).forEach((key) => {
            if (!keys.includes(key)) keys.push(key);
        });
        return keys;
    }, []);

    return [
        ...schemaKeys,
        ...rowKeys.filter((key) => !schemaKeys.includes(key))
    ];
}

// Memoized so typing in the result panel never re-renders the pdf/image viewer.
const DocumentPreview = memo(function DocumentPreview({
    fileType,
    fileUrl,
    fileName,
    pagesData,
    fileDocumentInfo,
    handleFieldClick,
    selectedKey,
    fieldRefs,
    inputRefs,
    showHighlights,
    setShowHighlights,
    noCoordinates,
}) {
    if (fileType === 'pdf') {
        if (pagesData.length === 0) {
            return (
                <iframe
                    title={fileName}
                    src={fileUrl}
                    className='h-full w-full bg-slate-50'
                    frameBorder="0"
                    allowFullScreen
                ></iframe>
            );
        }

        return (
            <PDFViewerWithHighlights
                pdfUrl={fileUrl}
                pages={pagesData}
                onFieldClick={handleFieldClick}
                selectedKey={selectedKey}
                fieldRefs={fieldRefs}
                inputRefs={inputRefs}
                showHighlights={showHighlights}
                setShowHighlights={setShowHighlights}
                noCoordinates={noCoordinates}
            />
        );
    }

    if (fileType === 'image') {
        if (pagesData.length === 0) {
            return (
                <img
                    src={fileUrl}
                    alt={fileName}
                    className="h-full w-full bg-slate-50 object-contain"
                />
            );
        }

        return (
            <ImageViewerWithHighlights
                imageUrl={fileUrl}
                fields={pagesData[0].fields}
                originalWidth={fileDocumentInfo.pages[0].width}
                originalHeight={fileDocumentInfo.pages[0].height}
                onFieldClick={handleFieldClick}
                selectedKey={selectedKey}
                fieldRefs={fieldRefs}
                inputRefs={inputRefs}
                showHighlights={showHighlights}
                setShowHighlights={setShowHighlights}
                noCoordinates={noCoordinates}
            />
        );
    }

    if (fileType === 'heic') {
        return (
            <div className="flex h-full w-full items-center justify-center bg-slate-50">
                <div className="text-center">
                    <p className="mb-2 text-gray-500">HEIC format not supported in browser</p>
                    <button
                        onClick={() => window.open(fileUrl, '_blank')}
                        className="text-indigo-600 hover:text-indigo-700"
                    >
                        Download to view
                    </button>
                </div>
            </div>
        );
    }

    return null;
});

function ExpandedDocumentView({ open, setOpen, fileName, children }) {
    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-100" onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900 bg-opacity-40 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-100">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel className="flex h-screen w-screen flex-col overflow-hidden bg-white text-left shadow-xl">
                            <div className="flex flex-col gap-3 border-b border-gray-200 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                                <div className="min-w-0">
                                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                                        Document
                                    </Dialog.Title>
                                    <p className="mt-1 truncate text-sm text-gray-500">
                                        {fileName}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-gray-700"
                                    onClick={() => setOpen(false)}
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                            </div>

                            <div className="min-h-0 flex-1 overflow-auto bg-slate-100">
                                {children}
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

function ExpandedResultView({
    open,
    setOpen,
    fileName,
    result,
    extraction,
    handleInputChangeString,
    handleInputChangeObject,
    handleInputChangeArrayString,
    handleInputChangeArrayObject,
    addInputArrayString,
    addInputArrayObject,
    deleteInputArrayString,
    deleteInputArrayObject,
    hasDataChanged,
    saveChanges,
    isLoading,
    onInputFocus
}) {
    const hasChanges = hasDataChanged();

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-100" onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900 bg-opacity-40 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-100">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-150"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel className="flex h-screen w-screen flex-col overflow-hidden bg-white text-left shadow-xl">
                            <div className="flex flex-col gap-3 border-b border-gray-200 bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                                <div className="min-w-0">
                                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                                        Table view
                                    </Dialog.Title>
                                    <p className="mt-1 truncate text-sm text-gray-500">
                                        {fileName}
                                    </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={saveChanges}
                                        disabled={!hasChanges || isLoading}
                                        className={classNames(
                                            "inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
                                            (!hasChanges || isLoading) && "opacity-50 cursor-not-allowed"
                                        )}
                                    >
                                        Save result
                                    </button>
                                    <button
                                        type="button"
                                        className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-500 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:text-gray-700"
                                        onClick={() => setOpen(false)}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>

                            <div className="flex-1 overflow-auto bg-slate-50 px-4 py-5 sm:px-6">
                                <div className="mx-auto flex max-w-7xl flex-col gap-5">
                                    {extraction.fields.map((field, index) => (
                                        <ExpandedField
                                            key={field.key ?? index}
                                            field={field}
                                            value={result[field.key]}
                                            handleInputChangeString={handleInputChangeString}
                                            handleInputChangeObject={handleInputChangeObject}
                                            handleInputChangeArrayString={handleInputChangeArrayString}
                                            handleInputChangeArrayObject={handleInputChangeArrayObject}
                                            addInputArrayString={addInputArrayString}
                                            addInputArrayObject={addInputArrayObject}
                                            deleteInputArrayString={deleteInputArrayString}
                                            deleteInputArrayObject={deleteInputArrayObject}
                                            onInputFocus={onInputFocus}
                                        />
                                    ))}
                                </div>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

const ExpandedField = memo(function ExpandedField({
    field,
    value,
    handleInputChangeString,
    handleInputChangeObject,
    handleInputChangeArrayString,
    handleInputChangeArrayObject,
    addInputArrayString,
    addInputArrayObject,
    deleteInputArrayString,
    deleteInputArrayObject,
    onInputFocus
}) {
    const fieldType = field.type ?? 'string';
    const itemsType = field.items?.type ?? 'string';
    const key = field.key;

    if (fieldType === "string") {
        return (
            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                    <h4 className="text-sm font-semibold text-gray-900">{key}</h4>
                </div>
                <div className="p-4">
                    <textarea
                        onFocus={() => onInputFocus?.(key)}
                        onChange={(e) => handleInputChangeString(key, e.target.value)}
                        value={value ?? ''}
                        rows={3}
                        className="block w-full resize-y rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        placeholder="add a value"
                    />
                </div>
            </section>
        )
    }

    if (fieldType === "object") {
        const objectData = value ?? {};
        const orderedKeys = getSchemaOrderedKeys(objectData, field.properties ?? []);

        return (
            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                    <h4 className="text-sm font-semibold text-gray-900">{key}</h4>
                </div>
                <div className="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
                    {orderedKeys.map((innerKey) => (
                        <label key={innerKey} className="block">
                            <span className="mb-1 block text-xs font-medium text-gray-600">{innerKey}</span>
                            <input
                                onFocus={() => onInputFocus?.(key + "_" + innerKey)}
                                onChange={(e) => handleInputChangeObject(key, innerKey, e.target.value)}
                                type="text"
                                value={objectData[innerKey] ?? ''}
                                className="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                placeholder="add a value"
                            />
                        </label>
                    ))}
                </div>
            </section>
        )
    }

    if (fieldType === "array" && itemsType === "string") {
        const rows = Array.isArray(value) ? value : [];

        return (
            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h4 className="text-sm font-semibold text-gray-900">{key}</h4>
                        <p className="mt-1 text-xs text-gray-500">{rows.length} {rows.length === 1 ? "item" : "items"}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => addInputArrayString(key)}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-indigo-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        Add row
                    </button>
                </div>
                <div className="overflow-auto">
                    <table className="min-w-full border-separate border-spacing-0 text-sm">
                        <thead>
                            <tr>
                                <th className="sticky left-0 top-0 z-10 w-16 border-b border-r border-gray-200 bg-gray-100 px-3 py-2 text-left text-xs font-semibold text-gray-600">
                                    #
                                </th>
                                <th className="sticky top-0 border-b border-gray-200 bg-gray-100 px-3 py-2 text-left text-xs font-semibold text-gray-600">
                                    Value
                                </th>
                                <th className="sticky right-0 top-0 z-10 w-20 border-b border-l border-gray-200 bg-gray-100 px-3 py-2 text-left text-xs font-semibold text-gray-600">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <ExpandedArrayStringRow
                                    key={rowIndex}
                                    fieldKey={key}
                                    index={rowIndex}
                                    value={row}
                                    canDelete={rows.length > 1}
                                    handleInputChangeArrayString={handleInputChangeArrayString}
                                    deleteInputArrayString={deleteInputArrayString}
                                    onInputFocus={onInputFocus}
                                />
                            ))}
                        </tbody>
                    </table>
                    {rows.length === 0 && (
                        <div className="px-4 py-6 text-sm text-gray-500">
                            No rows yet.
                        </div>
                    )}
                </div>
            </section>
        )
    }

    if (fieldType === "array" && itemsType === "object") {
        const rows = Array.isArray(value) ? value : [];
        const columns = getArrayObjectColumns(field, rows);

        return (
            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <div className="flex flex-col gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h4 className="text-sm font-semibold text-gray-900">{key}</h4>
                        <p className="mt-1 text-xs text-gray-500">{rows.length} {rows.length === 1 ? "row" : "rows"}</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => addInputArrayObject(key)}
                        className="inline-flex items-center justify-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-indigo-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        Add row
                    </button>
                </div>
                <div className="overflow-auto">
                    <table className="min-w-full border-separate border-spacing-0 text-sm">
                        <thead>
                            <tr>
                                <th className="sticky left-0 top-0 z-10 w-16 border-b border-r border-gray-200 bg-gray-100 px-3 py-2 text-left text-xs font-semibold text-gray-600">
                                    #
                                </th>
                                {columns.map((column) => (
                                    <th
                                        key={column}
                                        className="sticky top-0 min-w-[220px] border-b border-r border-gray-200 bg-gray-100 px-3 py-2 text-left text-xs font-semibold text-gray-600"
                                    >
                                        {column}
                                    </th>
                                ))}
                                <th className="sticky right-0 top-0 z-10 w-20 border-b border-l border-gray-200 bg-gray-100 px-3 py-2 text-left text-xs font-semibold text-gray-600">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <ExpandedArrayObjectRow
                                    key={rowIndex}
                                    fieldKey={key}
                                    index={rowIndex}
                                    row={row}
                                    columns={columns}
                                    canDelete={rows.length > 1}
                                    handleInputChangeArrayObject={handleInputChangeArrayObject}
                                    deleteInputArrayObject={deleteInputArrayObject}
                                    onInputFocus={onInputFocus}
                                />
                            ))}
                        </tbody>
                    </table>
                    {rows.length === 0 && (
                        <div className="px-4 py-6 text-sm text-gray-500">
                            No rows yet.
                        </div>
                    )}
                </div>
            </section>
        )
    }

    return null;
});

const ExpandedArrayStringRow = memo(function ExpandedArrayStringRow({
    fieldKey,
    index,
    value,
    canDelete,
    handleInputChangeArrayString,
    deleteInputArrayString,
    onInputFocus
}) {
    return (
        <tr>
            <td className="sticky left-0 border-b border-r border-gray-200 bg-white px-3 py-2 font-medium text-gray-700">
                {index + 1}
            </td>
            <td className="min-w-[360px] border-b border-gray-200 px-2 py-2">
                <input
                    onFocus={() => onInputFocus?.(fieldKey + "_" + (index + 1))}
                    onChange={(e) => handleInputChangeArrayString(fieldKey, index, e.target.value)}
                    type="text"
                    value={value ?? ''}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-transparent hover:ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                    placeholder="add a value"
                />
            </td>
            <td className="sticky right-0 border-b border-l border-gray-200 bg-white px-3 py-2">
                {canDelete && (
                    <button
                        type="button"
                        onClick={() => deleteInputArrayString(fieldKey, index)}
                        className="text-sm font-semibold text-red-600 hover:text-red-700"
                    >
                        Remove
                    </button>
                )}
            </td>
        </tr>
    )
});

function haveSameColumns(previousColumns, nextColumns) {
    return previousColumns.length === nextColumns.length
        && previousColumns.every((column, index) => column === nextColumns[index]);
}

const ExpandedArrayObjectRow = memo(function ExpandedArrayObjectRow({
    fieldKey,
    index,
    row,
    columns,
    canDelete,
    handleInputChangeArrayObject,
    deleteInputArrayObject,
    onInputFocus
}) {
    return (
        <tr>
            <td className="sticky left-0 border-b border-r border-gray-200 bg-white px-3 py-2 font-medium text-gray-700">
                {index + 1}
            </td>
            {columns.map((column) => (
                <td key={column} className="min-w-[220px] border-b border-r border-gray-200 px-2 py-2">
                    <input
                        onFocus={() => onInputFocus?.(fieldKey + "_" + (index + 1) + "_" + column)}
                        onChange={(e) => handleInputChangeArrayObject(fieldKey, index, column, e.target.value)}
                        type="text"
                        value={row?.[column] ?? ''}
                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-transparent hover:ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                        placeholder="add a value"
                    />
                </td>
            ))}
            <td className="sticky right-0 border-b border-l border-gray-200 bg-white px-3 py-2">
                {canDelete && (
                    <button
                        type="button"
                        onClick={() => deleteInputArrayObject(fieldKey, index)}
                        className="text-sm font-semibold text-red-600 hover:text-red-700"
                    >
                        Remove
                    </button>
                )}
            </td>
        </tr>
    )
}, (previous, next) => (
    // The column list is rebuilt on every render, so it has to be compared by content.
    previous.fieldKey === next.fieldKey
    && previous.index === next.index
    && previous.row === next.row
    && previous.canDelete === next.canDelete
    && previous.handleInputChangeArrayObject === next.handleInputChangeArrayObject
    && previous.deleteInputArrayObject === next.deleteInputArrayObject
    && previous.onInputFocus === next.onInputFocus
    && haveSameColumns(previous.columns, next.columns)
));

// Ref callbacks must keep the same identity across renders, otherwise every render
// detaches and re-attaches every input ref.
function useInputRefSetter(inputRefs) {
    const setters = useRef({});

    return useCallback((key) => {
        if (!setters.current[key]) {
            setters.current[key] = (element) => {
                if (element) {
                    inputRefs.current[key] = element;
                } else {
                    delete inputRefs.current[key];
                }
            };
        }

        return setters.current[key];
    }, [inputRefs]);
}

function DisplayResult({
    result,
    extraction,
    handleInputChangeString,
    handleInputChangeObject,
    handleInputChangeArrayString,
    handleInputChangeArrayObject,
    addInputArrayString,
    addInputArrayObject,
    deleteInputArrayString,
    deleteInputArrayObject,
    isLoading,
    inputRefs,
    onInputFocus
}) {
    const getInputRef = useInputRefSetter(inputRefs);

    if (isLoading) {
        return (
            <>
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="mb-4 flex flex-col gap-x-2 rounded-lg border border-gray-200 bg-slate-50 p-3 sm:p-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            <span className="w-full font-bold break-words sm:w-1/2 sm:pr-4">
                                <Skeleton />
                            </span>
                            <div className="relative w-full sm:w-1/2">
                                <Skeleton />
                            </div>
                        </div>
                    </div>
                ))}
            </>
        )
    }

    return (
        <div className='flex flex-col gap-y-4'>
            {extraction.fields.map((field, index) => (
                <ResultField
                    key={field.key ?? index}
                    field={field}
                    value={result[field.key]}
                    handleInputChangeString={handleInputChangeString}
                    handleInputChangeObject={handleInputChangeObject}
                    handleInputChangeArrayString={handleInputChangeArrayString}
                    handleInputChangeArrayObject={handleInputChangeArrayObject}
                    addInputArrayString={addInputArrayString}
                    addInputArrayObject={addInputArrayObject}
                    deleteInputArrayString={deleteInputArrayString}
                    deleteInputArrayObject={deleteInputArrayObject}
                    getInputRef={getInputRef}
                    onInputFocus={onInputFocus}
                />
            ))}
        </div>
    )
}

// Each field only receives its own value, so editing one field leaves the others untouched.
const ResultField = memo(function ResultField({
    field,
    value,
    handleInputChangeString,
    handleInputChangeObject,
    handleInputChangeArrayString,
    handleInputChangeArrayObject,
    addInputArrayString,
    addInputArrayObject,
    deleteInputArrayString,
    deleteInputArrayObject,
    getInputRef,
    onInputFocus
}) {
    const fieldType = field.type ?? 'string';
    const itemsType = field.items?.type ?? 'string';
    const key = field.key;

    if (fieldType === "string") {
        const resultData = value ?? '';

        return (
            <div className="flex flex-col gap-x-2 rounded-lg border border-gray-200 bg-slate-50 p-3 sm:p-4">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <span className="w-full font-bold break-words text-gray-900 sm:w-1/2 sm:pr-4">
                        {key}
                    </span>
                    <div className="relative w-full sm:w-1/2">
                        <input
                            ref={getInputRef(key)}
                            onFocus={() => {
                                onInputFocus?.(key);
                            }}
                            onChange={(e) => {
                                e.preventDefault();
                                handleInputChangeString(key, e.target.value);
                            }}
                            type="text"
                            name="value"
                            id="value"
                            value={resultData}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            placeholder="add a value"
                            required
                        />
                    </div>
                </div>
            </div>
        )
    }

    if (fieldType === "object") {
        const resultData = value ?? {};

        // Extract and sort the keys based on their IDs from field.properties
        const sortedInnerKeys = Object.keys(resultData).sort((a, b) => {
            const propertyA = field.properties.find(property => property.key === a);
            const propertyB = field.properties.find(property => property.key === b);

            const idA = propertyA ? propertyA.id : Number.MAX_SAFE_INTEGER;
            const idB = propertyB ? propertyB.id : Number.MAX_SAFE_INTEGER;

            return idA - idB;
        });

        return (
            <div className="flex flex-col gap-x-2 rounded-lg border border-gray-200 bg-slate-50 p-3 sm:p-4">
                <div className="mb-4">
                    <span className="font-bold">
                        {key}
                    </span>
                </div>
                <div className="flex flex-col gap-y-3.5 sm:pl-4">
                    {sortedInnerKeys.map((innerKey) => {
                        return (
                            <div className="flex items-center" key={innerKey}>
                                <span
                                    className={classNames(
                                        'inline-block h-2 w-2 flex-shrink-0 rounded-full mr-3.5',
                                        true ? 'bg-indigo-500' : 'bg-gray-200'
                                    )}
                                    aria-hidden="true"
                                />
                                <div className="relative w-full">
                                    <label
                                        htmlFor="name"
                                        className="absolute -top-2 left-2 inline-block bg-white rounded px-1 text-xs font-medium text-gray-900"
                                    >
                                        {innerKey}
                                    </label>
                                    <input
                                        ref={getInputRef(key + "_" + innerKey)}
                                        onFocus={() => {
                                            onInputFocus?.(key + "_" + innerKey);
                                        }}
                                        onChange={(e) => {
                                            e.preventDefault();
                                            handleInputChangeObject(key, innerKey, e.target.value);
                                        }}
                                        type="text"
                                        name="value"
                                        id="value"
                                        value={resultData[innerKey]}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        placeholder="add a value"
                                        required
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    if (fieldType === "array" && itemsType === "string") {
        const resultData = Array.isArray(value) ? value : [];
        const numberOfItems = resultData.length;

        return (
            <div className="flex flex-col gap-x-2 rounded-lg border border-gray-200 bg-slate-50 p-3 sm:p-4">
                <div className="mb-3">
                    <div className="flex items-center justify-between">
                        <span className="font-bold">
                            {key}
                        </span>
                        <span className="text-sm text-gray-500">
                            {numberOfItems} {numberOfItems === 1 ? "item" : "items"}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col gap-y-3.5 sm:pl-4">
                    {resultData.map((item, itemIndex) => (
                        <ResultArrayStringRow
                            key={itemIndex}
                            fieldKey={key}
                            index={itemIndex}
                            value={item}
                            canDelete={resultData.length > 1}
                            handleInputChangeArrayString={handleInputChangeArrayString}
                            deleteInputArrayString={deleteInputArrayString}
                            getInputRef={getInputRef}
                            onInputFocus={onInputFocus}
                        />
                    ))}
                    <button
                        onClick={() => addInputArrayString(key)}
                        className="pl-7 inline-flex items-center gap-x-1.5 text-sm font-semibold text-indigo-600 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        Add item
                    </button>
                </div>
            </div>
        )
    }

    if (fieldType === "array" && itemsType === "object") {
        const resultData = Array.isArray(value) ? value : [];
        const numberOfItems = resultData.length;

        return (
            <div className="flex flex-col gap-x-2 rounded-lg border border-gray-200 bg-slate-50 p-3 sm:p-4">
                <div className="mb-4">
                    <div className="flex items-center justify-between">
                        <span className="font-bold">
                            {key}
                        </span>
                        <span className="text-sm text-gray-500">
                            {numberOfItems} {numberOfItems === 1 ? "item" : "items"}
                        </span>
                    </div>
                </div>
                <div className="flex flex-col gap-y-3.5 sm:pl-4">
                    {resultData.map((item, itemIndex) => (
                        <ResultArrayObjectRow
                            key={itemIndex}
                            field={field}
                            fieldKey={key}
                            index={itemIndex}
                            objectData={item}
                            isLast={itemIndex === resultData.length - 1}
                            canDelete={resultData.length > 1}
                            handleInputChangeArrayObject={handleInputChangeArrayObject}
                            deleteInputArrayObject={deleteInputArrayObject}
                            getInputRef={getInputRef}
                            onInputFocus={onInputFocus}
                        />
                    ))}
                    <button
                        onClick={() => addInputArrayObject(key)}
                        className="pl-7 inline-flex items-center gap-x-1.5 text-sm font-semibold text-indigo-600 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        Add item
                    </button>
                </div>
            </div>
        )
    }

    return null;
});

const ResultArrayStringRow = memo(function ResultArrayStringRow({
    fieldKey,
    index,
    value,
    canDelete,
    handleInputChangeArrayString,
    deleteInputArrayString,
    getInputRef,
    onInputFocus
}) {
    const refKey = fieldKey + "_" + (index + 1);

    return (
        <div className="flex items-center">
            <span className='mr-2.5 w-5 flex-shrink-0 text-right font-bold text-indigo-500'>
                {index + 1}.
            </span>
            <div className="relative w-full">
                <input
                    ref={getInputRef(refKey)}
                    onFocus={() => {
                        onInputFocus?.(refKey);
                    }}
                    onChange={(e) => {
                        e.preventDefault();
                        handleInputChangeArrayString(fieldKey, index, e.target.value);
                    }}
                    type="text"
                    name="value"
                    id="value"
                    value={value ?? ''}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="add a value"
                    required
                />
            </div>
            {/* remove button if there are multiple items */}
            {canDelete && (
                <button
                    onClick={() => deleteInputArrayString(fieldKey, index)}
                    className={classNames(
                        'bg-red-400 hover:bg-red-500 w-4 h-4 flex items-center justify-center rounded-full ml-3 p-1',
                    )}
                    aria-label="Remove"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                    </svg>
                </button>
            )}
        </div>
    )
});

const ResultArrayObjectRow = memo(function ResultArrayObjectRow({
    field,
    fieldKey,
    index,
    objectData,
    isLast,
    canDelete,
    handleInputChangeArrayObject,
    deleteInputArrayObject,
    getInputRef,
    onInputFocus
}) {
    const defaultId = Number.MAX_SAFE_INTEGER; // A large number to ensure properties without IDs are at the end

    const sortedKeys = Object.keys(objectData).sort((a, b) => {
        const propertyA = field.items.properties.find(property => property.key === a);
        const propertyB = field.items.properties.find(property => property.key === b);

        const idA = propertyA && propertyA.id !== undefined ? propertyA.id : defaultId;
        const idB = propertyB && propertyB.id !== undefined ? propertyB.id : defaultId;

        return idA - idB;
    });

    return (
        <div className="flex items-start">
            <span className='mr-3 w-5 flex-shrink-0 text-right font-bold text-indigo-500 sm:mr-4'>
                {index + 1}.
            </span>
            <div className="w-full flex flex-col gap-y-4">
                {sortedKeys.map((innerKey) => {
                    const refKey = fieldKey + "_" + (index + 1) + "_" + innerKey;

                    return (
                        <div className="flex items-center" key={innerKey}>
                            <div className="relative w-full">
                                <label
                                    htmlFor="name"
                                    className="absolute -top-2 left-2 inline-block bg-white rounded px-1 text-xs font-medium text-gray-900"
                                >
                                    {innerKey}
                                </label>
                                <input
                                    ref={getInputRef(refKey)}
                                    onFocus={() => {
                                        onInputFocus?.(refKey);
                                    }}
                                    onChange={(e) => {
                                        e.preventDefault();
                                        handleInputChangeArrayObject(fieldKey, index, innerKey, e.target.value);
                                    }}
                                    type="text"
                                    name="value"
                                    id="value"
                                    value={objectData[innerKey]}
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                    placeholder="add a value"
                                    required
                                />
                            </div>
                        </div>
                    )
                })}
                {/* add divider if object data is not the last one */}
                {!isLast && (
                    <div className="border-t border-gray-200 mb-1"></div>
                )}
            </div>
            {canDelete && (
                <button
                    onClick={() => deleteInputArrayObject(fieldKey, index)}
                    className={classNames(
                        'bg-red-400 hover:bg-red-500 w-4 h-4 flex items-center justify-center rounded-full ml-3 p-1 mt-2',
                    )}
                    aria-label="Remove"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                    </svg>
                </button>
            )}
        </div>
    )
});

export default ViewFile;
